import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components";
import { AuthLayout } from "@/features/auth/components/AuthLayout";
import { useAuth } from "@/features/auth/context/AuthContext";
import { authService } from "@/features/auth/services/auth.service";
import toast from "react-hot-toast";
import { Mail, CheckCircle2, ArrowRight } from "lucide-react";

export const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyEmail } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const hasVerifiedRef = useRef(false);

  const token = searchParams.get("token");

  useEffect(() => {
    // Get email from location state or search params
    const stateEmail = (window.history.state?.usr as any)?.email;
    setEmail(stateEmail || searchParams.get("email"));
  }, [searchParams]);

  const handleVerify = async () => {
    if (!token || hasVerifiedRef.current) return;

    hasVerifiedRef.current = true;
    setIsLoading(true);
    try {
      await verifyEmail(token);
      setIsVerified(true);
      toast.success("Email verified successfully!");
      setTimeout(() => {
        navigate("/home/content-wizard");
      }, 2000);
    } catch (error: any) {
      hasVerifiedRef.current = false; // Allow retry on error
      toast.error(error || "Failed to verify email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token && !hasVerifiedRef.current && !isVerified && !isLoading) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isVerified, isLoading]);

  const handleResend = async () => {
    if (!email) {
      toast.error("Email address not found");
      return;
    }

    setIsLoading(true);
    try {
      await authService.resendVerificationEmail(email);
      toast.success("Verification email sent!");
    } catch (error: any) {
      toast.error(
        error || "Failed to send verification email. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerified) {
    return (
      <AuthLayout>
        <div className="space-y-8 text-center">
          <div className="flex justify-center">
            <div className="rounded-full bg-green-500/10 p-4">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold">Email verified!</h1>
            <p className="text-muted-foreground">
              Your email has been successfully verified. Redirecting to
              dashboard...
            </p>
          </div>
          <Link to="/home/content-wizard">
            <Button className="w-full">
              Go to Dashboard
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (token) {
    return (
      <AuthLayout>
        <div className="space-y-8 text-center">
          <div className="flex justify-center">
            <div className="rounded-full bg-primary/10 p-4">
              <Mail className="h-8 w-8 text-primary" />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold">Verifying email...</h1>
            <p className="text-muted-foreground">
              Please wait while we verify your email address
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="space-y-8 text-center">
        <div className="flex justify-center">
          <div className="rounded-full bg-primary/10 p-4">
            <Mail className="h-8 w-8 text-primary" />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-3xl font-bold">Verify your email</h1>
          <p className="text-muted-foreground">
            {email
              ? `We've sent a verification link to ${email}`
              : "Please check your email for a verification link"}
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            Didn't receive the email? Check your spam folder or resend the
            verification email.
          </p>
        </div>
        <div className="space-y-3">
          <Button
            onClick={handleResend}
            disabled={isLoading || !email}
            className="w-full"
          >
            {isLoading ? "Sending..." : "Resend verification email"}
          </Button>
          <Link to="/login">
            <Button variant="ghost" className="w-full">
              Back to login
            </Button>
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};
